import {
  MAX_STORED_COORDINATE,
  RECORDING_TOAST_DEFAULT_BOTTOM,
  RECORDING_TOAST_DEFAULT_LEFT,
  RECORDING_TOAST_POSITION_STORAGE_KEY_PREFIX,
  RECORDING_TOAST_VIEWPORT_MARGIN,
  RecordingToastPosition,
  RecordingToastSize,
  ViewportSize,
} from '../config';

export const getRecordingToastPositionStorageKey = (userID: string) =>
  `${RECORDING_TOAST_POSITION_STORAGE_KEY_PREFIX}:${userID}`;

const isFiniteCoordinate = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const parseStoredRecordingToastPosition = (
  storedPosition: string | null,
): RecordingToastPosition | null => {
  if (!storedPosition) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      storedPosition,
    ) as Partial<RecordingToastPosition>;

    if (!isFiniteCoordinate(parsed.x) || !isFiniteCoordinate(parsed.y)) {
      return null;
    }

    if (
      parsed.x < 0 ||
      parsed.y < 0 ||
      parsed.x > MAX_STORED_COORDINATE ||
      parsed.y > MAX_STORED_COORDINATE
    ) {
      return null;
    }

    return {
      x: parsed.x,
      y: parsed.y,
    };
  } catch {
    return null;
  }
};

export const clampRecordingToastPosition = ({
  margin = RECORDING_TOAST_VIEWPORT_MARGIN,
  position,
  size,
  viewport,
}: {
  margin?: number;
  position: RecordingToastPosition;
  size: RecordingToastSize;
  viewport: ViewportSize;
}): RecordingToastPosition => {
  const maxX = Math.max(margin, viewport.width - size.width - margin);
  const maxY = Math.max(margin, viewport.height - size.height - margin);

  return {
    x: Math.min(Math.max(position.x, margin), maxX),
    y: Math.min(Math.max(position.y, margin), maxY),
  };
};

export const getDefaultRecordingToastPosition = ({
  size,
  viewport,
}: {
  size: RecordingToastSize;
  viewport: ViewportSize;
}): RecordingToastPosition =>
  clampRecordingToastPosition({
    position: {
      x: RECORDING_TOAST_DEFAULT_LEFT,
      y: viewport.height - size.height - RECORDING_TOAST_DEFAULT_BOTTOM,
    },
    size,
    viewport,
  });

export const readRecordingToastPosition = ({
  storage,
  userID,
}: {
  storage: Storage;
  userID: string;
}): RecordingToastPosition | null => {
  try {
    return parseStoredRecordingToastPosition(
      storage.getItem(getRecordingToastPositionStorageKey(userID)),
    );
  } catch {
    return null;
  }
};

export const saveRecordingToastPosition = ({
  position,
  storage,
  userID,
}: {
  position: RecordingToastPosition;
  storage: Storage;
  userID: string;
}) => {
  try {
    storage.setItem(
      getRecordingToastPositionStorageKey(userID),
      JSON.stringify(position),
    );
  } catch {
    // Local persistence is best effort; recording controls must keep working.
  }
};
